import React, { CSSProperties } from 'react'
import { useGlobal } from '../../hooks/useGlobal'
import { useResponsive, useScrollAtTop } from '../../hooks/useResponsive'
import { buildFileUrl } from '../../config/strapi'

/**
 * Tipos para os dados de imagem e botão vindos do Strapi
 */
interface ImageData {
  url: string
  alternativeText?: string
}

interface ButtonData {
  title?: string
  link?: string
}

/**
 * Componente LogoSection responsivo
 * 
 * Exibe uma barra com:
 * - Primeira imagem (logo) à esquerda
 * - Segunda imagem (símbolo) centralizada
 * - Botão de ação à direita
 * 
 * Ordem na tela:
 * 1. HeaderEstadual - z-index 9999
 * 2. LogoSection (este componente) - z-index 998
 * 3. Navbar - z-index 997
 * 
 * Reason: Usa CSS Grid para garantir distribuição perfeita dos 3 elementos
 */
export function LogoSection() {
  const { data, loading, error } = useGlobal()
  const { 
    isSmallMobile, 
    isMobile,
    headerEstadualHeight,
    logoSectionHeight 
  } = useResponsive()
  const isAtTop = useScrollAtTop()

  // Estados de early return para loading/error
  if (loading || error || !data?.ImageBar) return null

  const { images = [], button } = data.ImageBar as { 
    images: ImageData[]
    button?: ButtonData 
  }

  // Reason: Verifica se cada imagem é SVG para aplicar dimensões explícitas
  // SVGs podem não ter dimensões intrínsecas definidas
  const isFirstImageSvg = images[0]?.url?.toLowerCase().endsWith('.svg')
  const isSecondImageSvg = images[1]?.url?.toLowerCase().endsWith('.svg')

  // Cálculos de dimensões
  const imageHeight = isSmallMobile ? 35 : isMobile ? 45 : 55
  const buttonPadding = isSmallMobile ? '6px 14px' : isMobile ? '7px 18px' : '10px 28px'
  const buttonFontSize = isSmallMobile ? '11px' : isMobile ? '13px' : '16px'
  const lateralPadding = isSmallMobile ? '12px' : isMobile ? '16px' : 'clamp(16px, 5vw, 7vw)'
  const gridGap = isSmallMobile ? '8px' : isMobile ? '16px' : '24px'

  // Posição top: logo abaixo do HeaderEstadual quando visível, esconde quando scroll
  const topPosition = isAtTop 
    ? `${headerEstadualHeight}px` 
    : `-${logoSectionHeight + 10}px`

  // Estilos do container principal
  const containerStyle: CSSProperties = {
    position: 'fixed',
    top: topPosition,
    left: 0,
    width: '100%',
    background: 'white',
    zIndex: 998,
    height: `${logoSectionHeight}px`,
    display: 'flex',
    alignItems: 'center',
    transition: 'top 0.3s ease-in-out',
    boxSizing: 'border-box'
  }

  // Estilos do wrapper interno
  const innerWrapperStyle: CSSProperties = {
    padding: `0 ${lateralPadding}`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 1700,
    margin: '0 auto',
    boxSizing: 'border-box'
  }

  // Reason: Flexbox permite que todos os elementos diminuam proporcionalmente
  // quando a tela é reduzida, evitando sobreposições
  const flexContainerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: gridGap
  }

  // Estilos para cada célula do flex container
  // Reason: flex: 1 com minWidth: 0 permite que as células diminuam
  const leftCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden'
  }

  const centerCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 1 auto',  // Reason: Não cresce, mas pode diminuir
    minWidth: 0,
    overflow: 'hidden'
  }

  const rightCellStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden'
  }

  // Reason: Estilo base para todas as imagens - diminuem proporcionalmente com a tela
  // Usando maxWidth em vw garante que ambas diminuam quando a tela diminui
  const baseImageStyle: CSSProperties = {
    height: 'auto',
    maxHeight: `${imageHeight}px`,
    width: 'auto',
    maxWidth: isSmallMobile ? '25vw' : isMobile ? '30vw' : '100%',
    objectFit: 'contain',
    flexShrink: 1  // Permite diminuir proporcionalmente
  }

  // Reason: Alias para manter consistência no código
  const imageStyle: CSSProperties = baseImageStyle
  const svgImageStyle: CSSProperties = baseImageStyle
  const centerImageStyle: CSSProperties = baseImageStyle
  const centerSvgImageStyle: CSSProperties = baseImageStyle

  // Estilos do botão
  const buttonStyle: CSSProperties = {
    background: '#1976d2',
    color: 'white',
    fontWeight: 500,
    fontSize: buttonFontSize,
    borderRadius: '24px',
    padding: buttonPadding,
    textDecoration: 'none',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    transition: 'background 0.2s',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    flexShrink: 0,
  }

  return (
    <div style={containerStyle}>
      <div style={innerWrapperStyle}>
        <div style={flexContainerStyle}>
          {/* Primeira imagem - Esquerda */}
          <div style={leftCellStyle}>
            {images[0] && (
              <img 
                src={buildFileUrl(images[0].url)}
                alt={images[0].alternativeText || 'Logo institucional'}
                style={isFirstImageSvg ? svgImageStyle : imageStyle}
              />
            )}
          </div>

          {/* Segunda imagem - Centro (Símbolo/Logo principal) */}
          <div style={centerCellStyle}>
            {images[1] && (
              <img 
                src={buildFileUrl(images[1].url)}
                alt={images[1].alternativeText || 'Logo CAR'}
                style={isSecondImageSvg ? centerSvgImageStyle : centerImageStyle}
              />
            )}
          </div>

          {/* Botão - Direita */}
          <div style={rightCellStyle}>
            {button && (
              <a
                href={button.link || '#'}
                style={buttonStyle}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = '#1565c0'
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLAnchorElement).style.background = '#1976d2'
                }}
              >
                {button.title || 'Acessar'}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
